import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { downloadBlob } from '@common/utils/downloadBlob';
import { apiClient } from '@config/server/apiClient';
import { preordersEndpoints } from '@config/server/apiEndpoints';

/**
 * Export preorders request parameters.
 */
export interface ExportPreordersParams {
	/** Optional list of preorder IDs to export. When omitted, all matching preorders are exported. */
	preorderIds?: string[];
}

/**
 * API function to export preorders as CSV.
 * Downloads the file to the user's machine.
 * @param servicesProviderId - The services provider to export preorders for
 * @param params - Export parameters including optional preorder ID filter
 */
export const exportPreorders = async (
	servicesProviderId: string,
	params: ExportPreordersParams,
): Promise<void> => {
	const { preorderIds } = params;

	// Use apiClient to get blob response
	const blob = await apiClient.postBlob(preordersEndpoints.export(servicesProviderId), {
		preorderIds,
	});

	downloadBlob(blob, 'preorders.csv');
};

/**
 * Hook for exporting preorders as CSV.
 * Wraps the export API call in a TanStack Query mutation with the current services provider context.
 */
export const useExportPreorders = (): UseMutationResult<void, Error, ExportPreordersParams> => {
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (params: ExportPreordersParams): Promise<void> => {
			if (!servicesProviderId) {
				throw new Error('Cannot export preorders without services provider ID');
			}
			return exportPreorders(servicesProviderId, params);
		},
	});
};
